import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { Alert } from '@cbhq/cds-web/overlays/Alert';

import { CustomAction } from '../../hooks/useCustomAction/useCustomAction.type';
import { Application } from '../../providers/Applications.types';
import { useApplicationsContext } from '../../providers/ApplicationsProvider';
import { useClientProps } from '../../providers/ClientPropsProvider';

import { generateCallToAction, getNotPrimeApps } from './utils';

type PrimeModalProviderType = {
  actions: CustomAction[];
};
type PrimeModalProviderFunction = () => PrimeModalProviderType;

export const PrimeModalContext = createContext<PrimeModalProviderFunction>(() => {
  throw new Error('PrimeModalContext used before being provided');
});

type PrimeModalProviderProps = { children: ReactNode };

// Prime project_id
const PRIME_PROJECT_ID = '2ULhRSFMybVYdhYMMPVI91riKEi';

export function PrimeModalProvider(props: PrimeModalProviderProps) {
  const { children } = props;
  const [visible, { toggleOn, toggleOff }] = useToggler();
  const applications = useApplicationsContext();
  const { projectId } = useClientProps();
  const [application, setApplication] = useState<Application | undefined>();

  const setApplicationById = useCallback(
    (applicationId: string) => {
      const selected = applications.find((app) => app.applicationId === applicationId);
      setApplication(selected);
    },
    [setApplication, applications],
  );

  const value = useCallback(() => {
    if (projectId !== PRIME_PROJECT_ID) return { actions: [] };
    const generateAction = generateCallToAction({ toggleOn, setApplicationById });
    const notPrimeApps = getNotPrimeApps(applications);
    const actions = notPrimeApps.map((app) => generateAction(app.applicationId));
    return { actions };
  }, [projectId, applications, toggleOn, setApplicationById]);

  const onConfirmAlert = useCallback(() => {
    if (application) {
      window.open(`${application.destinationUri}?appswitcher=true`, 'noopener');
    }
  }, [application]);

  return (
    <PrimeModalContext.Provider value={value}>
      {children}
      <Alert
        testID="prime-alert"
        pictogram="startToday"
        title="Leave Prime?"
        body="You are leaving Coinbase Prime"
        visible={visible}
        onRequestClose={toggleOff}
        preferredActionLabel="Leave"
        onPreferredActionPress={onConfirmAlert}
        dismissActionLabel="Cancel"
      />
    </PrimeModalContext.Provider>
  );
}

export function usePrimeModalProps(): PrimeModalProviderType {
  return useContext(PrimeModalContext)();
}
